// ###### IMPORTS
import React, { useEffect, useMemo, useState } from 'react';
import { WalletIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { useFinanceProfile } from 'views/finance/moduleServices/finance_profilecontext';
import { fetchFinancialFlows } from 'views/finance/moduleServices/flow_requests';
import { getBudgetBoard } from 'views/finance/tab_budget/viewServices/budget_api';

// --- Below: same helpers + layout classes as CardBudgetConsumed.jsx (replicated, not imported) ---

const formatValue = (value) => {
  const abs = Math.abs(Number(value) || 0);
  if (abs >= 1000) return `${(abs / 1000).toFixed(1)} k`;
  return abs.toFixed(1);
};

const formatSignedValue = (value) => {
  const n = Number(value) || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)} k`;
  return `${sign}${abs.toFixed(1)}`;
};

const getToneClasses = (value) => {
  const n = Number(value) || 0;
  if (n > 0) return { wrap: 'bg-green-50', text: 'text-green-700' };
  if (n < 0) return { wrap: 'bg-red-50', text: 'text-red-700' };
  return { wrap: 'bg-gray-100', text: 'text-gray-900' };
};

const FLOW_COLOR_IN = '#4ade80';
const FLOW_COLOR_OUT = '#f87171';
const FLOW_CHIP_IN = '#16a34a';
const FLOW_CHIP_OUT = '#dc2626';

const getDaysInMonth = (year, month01) => {
  const y = Number(year);
  const m = Number(month01);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;
  return new Date(y, m, 0).getDate();
};

const parseHyphenList = (value) => {
  if (value == null) return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split('-')
    .map((x) => x.trim())
    .filter(Boolean);
};

const getTotalDaysForSelection = (yearsValue, monthsValue) => {
  const years = parseHyphenList(yearsValue);
  const months = parseHyphenList(monthsValue);
  if (!years.length || !months.length) return 0;
  let total = 0;
  for (const y of years) {
    for (const m of months) {
      total += getDaysInMonth(y, m);
    }
  }
  return total;
};

const SELECTED_TYPE = 'Out';

async function fetchAllFlowsForPeriod(selectedYears, selectedMonths) {
  const limit = 5000;
  let offset = 0;
  let allFlows = [];
  let hasMore = true;
  const params = { years: selectedYears, months: selectedMonths, limit, offset };
  while (hasMore) {
    const data = await fetchFinancialFlows(params);
    allFlows = [...allFlows, ...data];
    hasMore = data.length === limit;
    offset += limit;
    params.offset = offset;
  }
  return allFlows;
}

// ###### MAIN COMPONENT
const CardOverviewBudget = ({ onNavigateToBudget }) => {
  const { profile } = useFinanceProfile();
  const filterLabel = profile?.default_categ_l0 || 'All';

  const { selectedYears, selectedMonths, monthHeader } = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear().toString();
    const m = (`0${d.getMonth() + 1}`).slice(-2);
    const monthHeader = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    return { selectedYears: y, selectedMonths: m, monthHeader };
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [boardData, setBoardData] = useState(null);
  const [flows, setFlows] = useState([]);

  useEffect(() => {
    const l0 = filterLabel === 'All' ? 'All' : filterLabel;
    const filters = {
      type: SELECTED_TYPE,
      years: selectedYears,
      months: selectedMonths,
      l0,
    };

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [boardRes, flowsList] = await Promise.all([
          getBudgetBoard(filters),
          fetchAllFlowsForPeriod(selectedYears, selectedMonths),
        ]);
        setBoardData(boardRes.data);
        setFlows(flowsList);
      } catch (err) {
        setError(err?.message || 'Failed to load budget.');
        setBoardData(null);
        setFlows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterLabel, selectedYears, selectedMonths]);

  const selectedType = SELECTED_TYPE;
  const flowBarColor = selectedType === 'In' ? FLOW_COLOR_IN : FLOW_COLOR_OUT;
  const flowChipColor = selectedType === 'In' ? FLOW_CHIP_IN : FLOW_CHIP_OUT;
  const summary = boardData?.summary;
  const totalFlow = summary?.total_flow != null ? Number(summary.total_flow) : 0;
  const totalBudget = summary?.total_budget != null ? Number(summary.total_budget) : 0;
  const achievementPct = summary?.achievement_pct != null ? Number(summary.achievement_pct) : 0;
  const flowDisplay = selectedType === 'Out' ? Math.abs(totalFlow) : totalFlow;
  const budgetDisplay = selectedType === 'Out' ? Math.abs(totalBudget) : totalBudget;
  const diff = budgetDisplay - flowDisplay;

  const pctClamped = Math.min(100, Math.max(0, achievementPct));
  const hasData = totalBudget !== 0 || totalFlow !== 0;

  const daysTotal = getTotalDaysForSelection(selectedYears, selectedMonths);
  const filteredFlows = useMemo(() => {
    return (flows || []).filter((flow) => {
      if (filterLabel && filterLabel !== 'All' && flow.categ_l0 !== filterLabel) return false;
      if (selectedType && flow.flow_type !== selectedType) return false;
      return true;
    });
  }, [flows, filterLabel, selectedType]);
  const daysWithFlow = useMemo(() => {
    if (!Array.isArray(filteredFlows) || filteredFlows.length === 0) return 0;
    const set = new Set();
    for (const f of filteredFlows) {
      if (f?.date) set.add(String(f.date));
    }
    return set.size;
  }, [filteredFlows]);
  const daysPct = daysTotal > 0 ? (daysWithFlow / daysTotal) * 100 : 0;

  return (
    <div
      className={`bg-white shadow rounded-xl overflow-hidden flex flex-col ${onNavigateToBudget ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''}`}
      onClick={() => onNavigateToBudget?.()}
      role={onNavigateToBudget ? 'button' : undefined}
      tabIndex={onNavigateToBudget ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onNavigateToBudget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigateToBudget();
        }
      }}
    >
      <div className="bg-green-800 border-b border-gray-200">
        <div className="px-3 py-1 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <WalletIcon className="h-5 w-5 text-white" />
            <h2 className="text-base font-semibold text-white capitalize">Budget</h2>
          </div>
          <div className="text-xs text-white opacity-70 truncate text-right max-w-[200px]">
            {filterLabel} · {monthHeader}
          </div>
        </div>
      </div>

      <div className="h-48 flex flex-col min-h-0 shrink-0">
        {loading && (
          <div className="h-full w-full p-3 relative overflow-hidden flex flex-col justify-center">
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded-full animate-pulse w-full" />
              <div className="flex gap-2">
                <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse" />
                <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse" />
                <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse" />
              </div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-36 mx-auto" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <SpinnerLoad />
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="h-full w-full flex items-center justify-center p-3">
            <div className="text-red-500 text-sm text-center px-2">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <div className="h-full w-full p-3 flex flex-col min-h-0">
            {hasData ? (
              <div className="h-full flex flex-col min-h-0 gap-2">
                <div className="relative min-h-[28px] flex items-center w-full shrink-0">
                  <div className="h-3 w-full min-w-0 rounded-full bg-gray-200 overflow-hidden flex shrink-0">
                    <div
                      className="h-full flex-shrink-0 transition-all duration-300 rounded-l-full"
                      style={{
                        width: `${pctClamped}%`,
                        minWidth: 0,
                        backgroundColor: flowBarColor,
                        borderTopRightRadius: pctClamped >= 100 ? 9999 : 0,
                        borderBottomRightRadius: pctClamped >= 100 ? 9999 : 0,
                      }}
                    />
                  </div>
                  <div
                    className="absolute top-1/2 z-0 pointer-events-none"
                    style={
                      pctClamped <= 0
                        ? { left: 0, transform: 'translate(0, -50%)' }
                        : pctClamped >= 100
                          ? { right: 0, transform: 'translate(0, -50%)' }
                          : {
                              left: `${pctClamped}%`,
                              transform: 'translate(-50%, -50%)',
                            }
                    }
                  >
                    <span
                      className="text-xs font-bold text-white rounded-md px-2 py-1 whitespace-nowrap shadow-sm"
                      style={{ backgroundColor: flowChipColor }}
                    >
                      {achievementPct.toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-h-0 flex items-center">
                  <div className="grid grid-cols-3 gap-2 w-full">
                    <div className="bg-gray-100 rounded-lg px-2 py-1.5 flex flex-col items-center text-center leading-tight">
                      <div className="text-base font-bold text-gray-900">{formatValue(flowDisplay)}</div>
                      <div className="text-xs text-gray-600">flow</div>
                    </div>
                    <div className="bg-gray-100 rounded-lg px-2 py-1.5 flex flex-col items-center text-center leading-tight">
                      <div className="text-base font-bold text-gray-900">{formatValue(budgetDisplay)}</div>
                      <div className="text-xs text-gray-600">budget</div>
                    </div>
                    <div
                      className={`${getToneClasses(diff).wrap} rounded-lg px-2 py-1.5 flex flex-col items-center text-center leading-tight`}
                    >
                      <div className={`text-base font-bold ${getToneClasses(diff).text}`}>
                        {formatSignedValue(diff)}
                      </div>
                      <div className="text-xs text-gray-600">diff</div>
                    </div>
                  </div>
                </div>

                <div className="text-sm font-semibold text-gray-700 text-center shrink-0">
                  {daysWithFlow} of {daysTotal} days ({daysPct.toFixed(1)}%)
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-gray-500 text-sm font-medium">No data available</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardOverviewBudget;
